import {
  Divider,
  ENotificationBadgeSize,
  GeneralAlertTriangleOutlineIcon,
  GeneralGiftIcon,
  GeneralGlobeIcon,
  GeneralHeartUnfilledIcon,
  GeneralHelpIcon,
  GeneralLogInLogOutIcon,
  GeneralShieldUnfilledIcon,
  GeneralSimplifiedListIcon,
  GeneralUnreadIcon,
  GeneralUserUserIcon,
  GeneralUserUserPlusIcon,
  NotificationBadge,
  VehiclesVehicleTownVehicleSimpleIcon,
} from '@outdoorsyco/bonfire';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useHostingRVFlow } from '@/components/route/become-a-host/RV/useHostingRVFlow';
import { referralPageAnchors } from '@/constants/anchors';
import { useRouter } from '@/hooks/useRouter';
import { useSoonestTripId } from '@/hooks/useSoonestTripId';
import { useUser } from '@/hooks/useUser';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { getUser } from '@/redux/selectors/auth/user';
import { trackNavClickedEvent } from '@/services/analytics/header';
import { formatCurrency } from '@/utility/currency';
import { openSessionModal } from '@/utility/session';

import { useGlobalHeaderMenuContext } from './GlobalHeaderMenuContext';
import { GlobalHeaderMenuItem } from './GlobalHeaderMenuItem';

const trackMenuItemClickedEvent = (clickedLink: string) => {
  trackNavClickedEvent({
    clickedLink,
    isInDropdown: true,
    dropdownHeader: 'My Account',
  });
};

const GlobalHeaderAuthedMenuItems = () => {
  const intl = useIntl();
  const router = useRouter();

  const user = useSelector(getUser);
  const soonestTripId = useSoonestTripId();
  const { redirectToHostingRVFlow } = useHostingRVFlow();
  const { logout } = useUser();

  const { openLanguageAndCurrencyModal, openGetHelpModal } = useGlobalHeaderMenuContext();

  return (
    <>
      <GlobalHeaderMenuItem
        icon={<GeneralHeartUnfilledIcon />}
        label={
          <FormattedMessage
            id="zI3UmD"
            defaultMessage="Wishlists"
            description="GlobalHeader > Menu > Wishlists"
          />
        }
        href="/wishlists"
        onClick={() => trackMenuItemClickedEvent('Wishlists')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralSimplifiedListIcon />}
        label={
          <FormattedMessage
            id="99zvOp"
            defaultMessage="Trips"
            description="GlobalHeader > Menu > Trips"
          />
        }
        href={soonestTripId ? `/trips/${soonestTripId}` : '/trips'}
        onClick={() => trackMenuItemClickedEvent('Trips')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralUnreadIcon />}
        label={
          <FormattedMessage
            id="SsvZa2"
            defaultMessage="Inbox"
            description="GlobalHeader > Menu > Inbox"
          />
        }
        badge={
          user?.unread_conversations ? (
            <span className="[&>div]:bg-bark-30">
              <NotificationBadge
                ariaLabel={intl.formatMessage({
                  id: '4y0UXm',
                  defaultMessage: 'Unread messages',
                  description: 'GlobalHeader > Menu > Inbox > Unread messages badge',
                })}
                count={user.unread_conversations}
                size={ENotificationBadgeSize.Small}
              />
            </span>
          ) : undefined
        }
        href="/messages"
        onClick={() => trackMenuItemClickedEvent('Inbox')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralUserUserIcon />}
        label={
          <FormattedMessage
            id="+NncbF"
            defaultMessage="Profile"
            description="GlobalHeader > Menu > Profile"
          />
        }
        badge={
          !user?.verifications?.email || !user?.verifications?.phone ? (
            <GeneralAlertTriangleOutlineIcon className="text-red-600" />
          ) : undefined
        }
        href="/account"
        onClick={() => trackMenuItemClickedEvent('Profile')}
      />

      <Divider />

      {(() => {
        if (user?.owner) {
          return (
            <GlobalHeaderMenuItem
              icon={<VehiclesVehicleTownVehicleSimpleIcon />}
              label={
                <FormattedMessage
                  id="hjWrAL"
                  defaultMessage="Switch to hosting"
                  description="GlobalHeader > Menu > Switch to hosting"
                />
              }
              href="/dashboard"
              onClick={() => trackMenuItemClickedEvent('Switch to hosting')}
              emphasized
            />
          );
        }

        if (router.pathname.startsWith('/become-a-host')) {
          return (
            <GlobalHeaderMenuItem
              icon={<VehiclesVehicleTownVehicleSimpleIcon />}
              label={
                <FormattedMessage
                  id="uhREcz"
                  defaultMessage="Try hosting today"
                  description="GlobalHeader > Menu > Try hosting today"
                />
              }
              onClick={() => {
                trackMenuItemClickedEvent('Try hosting today');
                redirectToHostingRVFlow();
              }}
              emphasized
            />
          );
        }

        return (
          <GlobalHeaderMenuItem
            icon={<VehiclesVehicleTownVehicleSimpleIcon />}
            label={
              <FormattedMessage
                id="FSt8f5"
                defaultMessage="Become a host"
                description="GlobalHeader > Menu > Become a host"
              />
            }
            href="/become-a-host"
            onClick={() => trackMenuItemClickedEvent('Become a host')}
            emphasized
          />
        );
      })()}

      <Divider />

      <GlobalHeaderMenuItem
        icon={<GeneralUserUserPlusIcon />}
        label={
          user?.credits ? (
            intl.formatMessage(
              {
                id: 'yA785Y',
                defaultMessage: 'Credits: {amount}',
                description: 'GlobalHeader > Menu > Refer a friend > Credits',
              },
              {
                amount: formatCurrency({
                  currency: user.currency || 'USD',
                  priceInCents: user.credits,
                  digits: 2,
                }),
              },
            )
          ) : (
            <FormattedMessage
              id="5VDPrF"
              defaultMessage="Refer a friend"
              description="GlobalHeader > Menu > Refer a friend"
            />
          )
        }
        href={
          // When the user has credits, deep-link to the travel credit balance section.
          user?.credits ? `/referral/renter#${referralPageAnchors.CREDITS}` : '/referral/renter'
        }
        onClick={() => trackMenuItemClickedEvent('Refer a friend')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralGiftIcon />}
        label={
          <FormattedMessage
            id="aoAdi+"
            defaultMessage="Gift cards"
            description="GlobalHeader > Menu > Gift cards"
          />
        }
        href="/gift-cards"
        onClick={() => trackMenuItemClickedEvent('Gift cards')}
      />

      <Divider />

      <GlobalHeaderMenuItem
        icon={<GeneralHelpIcon />}
        label={
          <FormattedMessage
            id="qANmDJ"
            defaultMessage="Get help"
            description="GlobalHeader > Menu > Get help"
          />
        }
        onClick={() => {
          trackMenuItemClickedEvent('Get help');
          openGetHelpModal();
        }}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralShieldUnfilledIcon />}
        label={
          <FormattedMessage
            id="hWh2f2"
            defaultMessage="Insurance & protection"
            description="GlobalHeader > Menu > Insurance & protection"
          />
        }
        href="https://support.outdoorsy.com/hc/en-us/articles/37646881475355-RV-Protection-Packages"
        onClick={() => trackMenuItemClickedEvent('Insurance & protection')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralGlobeIcon />}
        label={
          <FormattedMessage
            id="+RaOle"
            defaultMessage="Language & currency"
            description="GlobalHeader > Menu > Language & currency"
          />
        }
        onClick={() => openLanguageAndCurrencyModal()}
      />

      <Divider />

      <GlobalHeaderMenuItem
        icon={<GeneralLogInLogOutIcon />}
        label={
          <FormattedMessage
            id="sOzRKE"
            defaultMessage="Log out"
            description="GlobalHeader > Menu > Log out"
          />
        }
        onClick={() => {
          trackMenuItemClickedEvent('Log out');
          logout();
        }}
      />
    </>
  );
};

const GlobalHeaderGuestMenuItems = () => {
  const { openLanguageAndCurrencyModal, openGetHelpModal } = useGlobalHeaderMenuContext();

  return (
    <>
      <GlobalHeaderMenuItem
        label={
          <FormattedMessage
            id="bkQfPS"
            defaultMessage="Log in"
            description="GlobalHeader > Menu > Log in"
          />
        }
        onClick={() => openSessionModal('login')}
        emphasized
      />

      <GlobalHeaderMenuItem
        label={
          <FormattedMessage
            id="N76r6l"
            defaultMessage="Sign up"
            description="GlobalHeader > Menu > Sign up"
          />
        }
        onClick={() => openSessionModal('signup')}
      />

      <Divider />

      <GlobalHeaderMenuItem
        icon={<VehiclesVehicleTownVehicleSimpleIcon />}
        label={
          <FormattedMessage
            id="FSt8f5"
            defaultMessage="Become a host"
            description="GlobalHeader > Menu > Become a host"
          />
        }
        href="/become-a-host"
        onClick={() => trackMenuItemClickedEvent('Become a host')}
        emphasized
      />

      <Divider />

      <GlobalHeaderMenuItem
        icon={<GeneralGiftIcon />}
        label={
          <FormattedMessage
            id="aoAdi+"
            defaultMessage="Gift cards"
            description="GlobalHeader > Menu > Gift cards"
          />
        }
        href="/gift-cards"
        onClick={() => trackMenuItemClickedEvent('Gift cards')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralUserUserPlusIcon />}
        label={
          <FormattedMessage
            id="5VDPrF"
            defaultMessage="Refer a friend"
            description="GlobalHeader > Menu > Refer a friend"
          />
        }
        href="/referral/renter"
        onClick={() => trackMenuItemClickedEvent('Refer a friend')}
      />

      <Divider />

      <GlobalHeaderMenuItem
        icon={<GeneralHelpIcon />}
        label={
          <FormattedMessage
            id="qANmDJ"
            defaultMessage="Get help"
            description="GlobalHeader > Menu > Get help"
          />
        }
        onClick={() => {
          trackMenuItemClickedEvent('Get help');
          openGetHelpModal();
        }}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralShieldUnfilledIcon />}
        label={
          <FormattedMessage
            id="hWh2f2"
            defaultMessage="Insurance & protection"
            description="GlobalHeader > Menu > Insurance & protection"
          />
        }
        href="https://support.outdoorsy.com/hc/en-us/articles/37646881475355-RV-Protection-Packages"
        onClick={() => trackMenuItemClickedEvent('Insurance & protection')}
      />

      <GlobalHeaderMenuItem
        icon={<GeneralGlobeIcon />}
        label={
          <FormattedMessage
            id="+RaOle"
            defaultMessage="Language & currency"
            description="GlobalHeader > Menu > Language & currency"
          />
        }
        onClick={() => openLanguageAndCurrencyModal()}
      />
    </>
  );
};

export const GlobalHeaderMenuItems = () => {
  const isAuthenticated = useSelector(selectAuthenticated);

  if (isAuthenticated) {
    return <GlobalHeaderAuthedMenuItems />;
  }

  return <GlobalHeaderGuestMenuItems />;
};
